import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getRevenueChart, getRevenueChartCustom } from '../../../api/report.api';
import styles from '../../../assets/scss/pages/ReportCustomer.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatCurrency } from '../../../utils/formatters';

const PERIODS = [
    { key: 'today', label: 'Hôm nay' },
    { key: 'yesterday', label: 'Hôm qua' },
    { key: '7days', label: '7 ngày' },
    { key: 'thismonth', label: 'Tháng này' },
    { key: 'lastmonth', label: 'Tháng trước' },
    { key: 'custom', label: 'Tuỳ chọn' },
];

const VIEW_MODES = [
    { key: 'hour', label: 'Theo giờ' },
    { key: 'day', label: 'Theo ngày' },
    { key: 'weekday', label: 'Theo thứ' },
];

function formatDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
}

function formatShortCurrency(val) {
    if (val >= 1000000) return `${(val / 1000000).toFixed(1)}tr`;
    if (val >= 1000) return `${(val / 1000).toFixed(0)}k`;
    return String(val);
}

export default function ReportRevenue() {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canView = hasPermission(PERMISSIONS.REPORT.REVENUE_VIEW);

    const [searchParams] = useSearchParams();
    const urlPeriod = searchParams.get('period');
    const urlStart = searchParams.get('startDate');
    const urlEnd = searchParams.get('endDate');

    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState(urlPeriod || '7days');
    const [viewMode, setViewMode] = useState('weekday');
    const [chartData, setChartData] = useState([]);
    const [totalRevenue, setTotalRevenue] = useState(0);
    const [startDate, setStartDate] = useState(urlStart || formatDate(new Date()));
    const [endDate, setEndDate] = useState(urlEnd || formatDate(new Date()));

    const fetchIdRef = useRef(0);

    const fetchData = useCallback(async () => {
        if (!canView) { setLoading(false); return; }
        const fetchId = ++fetchIdRef.current;
        setLoading(true);
        try {
            let res;
            if (period === 'custom') {
                res = await getRevenueChartCustom({ startDate, endDate });
            } else {
                res = await getRevenueChart({ viewMode, period });
            }
            if (fetchId !== fetchIdRef.current) return;
            setChartData(res.data?.chartData || []);
            setTotalRevenue(res.data?.totalRevenue || 0);
        } catch (err) {
            if (fetchId !== fetchIdRef.current) return;
            setChartData([]);
            setTotalRevenue(0);
        } finally {
            if (fetchId === fetchIdRef.current) setLoading(false);
        }
    }, [canView, period, viewMode, startDate, endDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handlePeriodChange = (p) => {
        setPeriod(p);
        if (p !== 'custom') setViewMode('weekday');
    };

    const handleStartDateChange = (e) => {
        const val = e.target.value;
        setStartDate(val);
        if (val > endDate) setEndDate(val);
    };

    const handleEndDateChange = (e) => {
        const val = e.target.value;
        setEndDate(val);
        if (val < startDate) setStartDate(val);
    };

    // Donut chart data
    const donutData = [
        { name: 'Doanh thu', value: totalRevenue || 1 },
    ];

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ArrowLeft />
                </button>
                <h1 className={styles.title}>Báo cáo doanh thu</h1>
            </div>

            {/* Filters */}
            <div className={styles.filters}>
                <div className={styles.periodTabs}>
                    {PERIODS.map((p) => (
                        <button
                            key={p.key}
                            className={`${styles.periodTab}${period === p.key ? ` ${styles.periodTabActive}` : ''}`}
                            onClick={() => handlePeriodChange(p.key)}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                {period === 'custom' && (
                    <div className={styles.customDateRow}>
                        <div className={styles.dateGroup}>
                            <span className={styles.dateLabel}>Từ ngày:</span>
                            <input type="date" className={styles.dateInput} value={startDate} onChange={handleStartDateChange} />
                        </div>
                        <div className={styles.dateGroup}>
                            <span className={styles.dateLabel}>Đến ngày:</span>
                            <input type="date" className={styles.dateInput} value={endDate} onChange={handleEndDateChange} />
                        </div>
                    </div>
                )}

                {period !== 'custom' && (
                    <div className={styles.viewModeTabs}>
                        {VIEW_MODES.map((v) => (
                            <button
                                key={v.key}
                                className={`${styles.viewModeTab}${viewMode === v.key ? ` ${styles.viewModeTabActive}` : ''}`}
                                onClick={() => setViewMode(v.key)}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Donut: Doanh thu thuần */}
            <div className={styles.chartCard}>
                <div style={{ textAlign: 'center', marginBottom: 12, fontWeight: 700, fontSize: 16, color: '#1A1A1A' }}>
                    Doanh thu thuần
                </div>
                {loading ? (
                    <div className={styles.loading}><div className={styles.spinner} /></div>
                ) : (
                    <div style={{ position: 'relative', width: 260, height: 260, margin: '0 auto' }}>
                        <ResponsiveContainer width={260} height={260}>
                            <PieChart>
                                <Pie
                                    data={donutData}
                                    dataKey="value"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={80}
                                    outerRadius={120}
                                    startAngle={90}
                                    endAngle={-270}
                                    paddingAngle={0}
                                >
                                    <Cell fill="#0098F6" />
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                        <div style={{
                            position: 'absolute', top: '50%', left: '50%',
                            transform: 'translate(-50%, -50%)',
                            fontSize: 18, fontWeight: 700, color: '#0098F6', textAlign: 'center',
                            whiteSpace: 'nowrap',
                        }}>
                            {formatCurrency(totalRevenue)}
                        </div>
                    </div>
                )}
            </div>

            {/* Bar chart: Doanh thu */}
            <div className={styles.chartCard}>
                <div style={{ textAlign: 'center', marginBottom: 12, fontWeight: 700, fontSize: 16, color: '#1A1A1A' }}>
                    Doanh thu
                </div>
                {loading ? (
                    <div className={styles.loading}><div className={styles.spinner} /></div>
                ) : chartData.length === 0 ? (
                    <div className={styles.loading}>Không có dữ liệu trong khoảng thời gian này</div>
                ) : (
                    <div className={styles.chartWrap}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#ECEEF2" />
                                <XAxis
                                    dataKey="label"
                                    tick={{ fontSize: 12, fill: '#6B7280' }}
                                    tickLine={false}
                                    axisLine={{ stroke: '#ECEEF2' }}
                                />
                                <YAxis
                                    tick={{ fontSize: 11, fill: '#6B7280' }}
                                    tickLine={false}
                                    axisLine={false}
                                    tickFormatter={formatShortCurrency}
                                />
                                <Tooltip
                                    contentStyle={{
                                        borderRadius: 10,
                                        border: '1px solid #ECEEF2',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                        fontSize: 13,
                                    }}
                                    formatter={(value) => [formatCurrency(value), 'Doanh thu']}
                                />
                                <Bar dataKey="value" fill="#0098F6" radius={[6, 6, 0, 0]} maxBarSize={40} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
}
